import React, { useImperativeHandle, useRef, useState } from "react";
import { RCCAlert, RCCButton, RCCGrid, RCCHookToast, RCCServicesApi, RCCTooltip, arrayMove } from "react-common-components";
import Loader from "src/components/Loader";
import Title from "src/components/Title";
import { useTranslation } from "src/context/Translations/context";
import { standardErrorMessages } from "src/utils/errors";
import SortableContainer from "src/components/Sortable/Container";
import SortableItem from "src/components/Sortable/Item";
import CheckPermission from "src/components/CheckPermission";
import { PermissionEnum } from "src/context/Auth/interface";
import { useAuth } from "src/context/Auth/context";
import { restrictToVerticalAxis, restrictToWindowEdges } from "src/components/Sortable/Container/modifiers";
import { IMatbb001, IRefModalGraphic, IRefModalGrouper} from "../interface";
import Groupers from "./groupers";
import ModalGrouper from "./modalGrouper";
import ModalGrouperGraphic from "./modalGroupGraphic";
import ModalGraphic from "./modalGraphic";

export interface IRefContentMarkShare{
  loadData: (id: number) => void;
}

const ContentMarkShare: React.ForwardRefRenderFunction<IRefContentMarkShare> = (_, ref) => {

	const { translate } = useTranslation();
	const { addToast } = RCCHookToast();
	const {  administrator, superUser } = useAuth();

	const refModalGrouper = useRef<IRefModalGrouper>(null);
	const refModalGraphicGrouper = useRef<IRefModalGraphic>(null);
	const refModalGraphic = useRef<IRefModalGraphic>(null);

	const [ selectedMarketShare, setSelectedMarketShare] = useState<number | null>(null);
	const [ data, setData] = useState<IMatbb001 | null>(null);
	const [ tempData, setTempData] = useState<IMatbb001 | null>(null);
	const [loading, setIsLoading] = useState(true);
	const [waitingForResponse, setWaitingForResponse] = useState(false);

	const loadData = async (id: number) => {
		setSelectedMarketShare(id);
		try{
			setIsLoading(true);
			const response = await RCCServicesApi.getRequest({
				method: "MarketShare/SelectData",
				urlParameters: {
					id
				}
			});

			setData(response.data);
			setIsLoading(false);
		}catch(err){
			standardErrorMessages(err, translate("Erro ao buscar os dados"), addToast);
			setIsLoading(false);
		}
	};

	const onCallbackAddOrChange = () => {
		if(selectedMarketShare)
			loadData(selectedMarketShare);
	};

	const onCallbackDelete = (id: number) => {
		setData((prevState) => ({
			...prevState,
			matbb004: prevState?.matbb004.filter((item) => item.mab004Id !== id)
		} as IMatbb001));
	};

	const queryReorderGroupers = async (groupers: any[]) => {
		setWaitingForResponse(true);
		try{
			await RCCServicesApi.putRequest({
				method: "MarketShare/UpdateOrderGroupers",
				body: {
					rows: groupers
				}
			});
			setWaitingForResponse(false);
			
			addToast({
				color: "green",
				description: `${data?.mab001Labelagrupador} ${translate("movido com sucesso")}`,
				title: translate("Sucesso")
			});

			return true;
		}catch(err){
			setData(tempData);
			standardErrorMessages(err, `${translate("Erro ao mover")} ${data?.mab001Labelagrupador}`, addToast);
			setWaitingForResponse(false);
			return false;
		}
	};

	useImperativeHandle(ref, () => ({
		loadData
	}));

	return (
		<>
		
			<RCCGrid
				container 
				item
				justifyContent="space-between"
				xs={12}
			>
				<Title color="purple" size="xl">
					{data?.mab001Titulo}
				</Title>
				<RCCGrid container item xs={4} justifyContent="flex-end">
					{!loading && data && (
						<>
							<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE}>
								{(administrator || superUser || data.permission >= 2) && (
									<RCCTooltip
										text={ `${translate("Novo")} ${data.mab001Labelagrupador}`}
										position="leftEnd"
										color="purple"
									>
										<RCCButton
											icon={{name: "plus", color: "#FFF", size: 18}}
											color="purple"
											layout="isRounded"
											onClick={() => {refModalGrouper.current?.actionButtonAdd(data.mab001Id, data.mab001Labelagrupador);}}
											size="md"
										/>
									</RCCTooltip>
								)}
							</CheckPermission>
							<RCCTooltip
								text={translate("Visualizar Gráfico")}
								position="leftEnd"
								color="grey"
							>
								<RCCButton
									icon={{name: "chartBar", color: "#FFF", size: 18}}
									color="grey"
									layout="isRounded"
									onClick={(e) => {e.stopPropagation();  refModalGraphic.current?.openModalGraphic(data.mab001Id,  new Date(Date.UTC(parseInt(data.mab001Ano,10), 1, 0)).getFullYear() , data.mab001Titulo, data.mab001Labelagrupador); }}
									size="md"
								/>
							</RCCTooltip>
						</>
					)}
					
				</RCCGrid>
			</RCCGrid>
			{!loading && data && data.mab001Descricao && (
				<RCCGrid
					container
					item
					alignItems="center"
				>
					<Title color="grey" size="xs">
						{data.mab001Descricao}
					</Title>
				</RCCGrid>
			)}
			{loading ? (
				<Loader />
			) : (
				<>
					<RCCGrid styleInline="margin-top: 10px">
						{!data || data.matbb004.length === 0 ? 
							(
								<RCCGrid 
									container 
									styleInline="margin: 10px"
								>
									<RCCAlert 
										title={translate("Atenção")}
										description={translate("Nenhum {{agrupador}} encontrado", {
											agrupador: data?.mab001Labelagrupador
										})}
									/>
								</RCCGrid>
							) : (
								<>
									<SortableContainer
										onDragStart={() => {
											setTempData(data);
										}}
  									onDragEnd={(activeIdX, overId) => {
											if(activeIdX === overId)
												return; 	

											const activeIndex = data.matbb004.findIndex((checks) => checks.id === activeIdX);
											const overIndex = data.matbb004.findIndex((checks) => checks.id === overId);

											const orderedItems = arrayMove(data.matbb004, activeIndex, overIndex);

											setData((prevState) => ({
												...prevState,
												matbb004: orderedItems
											} as IMatbb001));

											const rowsOrdered: any[] = [];

											orderedItems.forEach((item: any, index) => {
												const row = {
													mab004Id: item.mab004Id,
													mab004Ordem: index
												};
												rowsOrdered.push(row);
											});

											queryReorderGroupers(rowsOrdered);
										}}
										items={data.matbb004.map((line) => ({
											...line,
											id: `${line.mab004Id.toString()}`
										}))}
										modifiers={[restrictToWindowEdges, restrictToVerticalAxis]}
  								>
										{data?.matbb004.map((grouper) => (
											<div key={`container-criteria-${grouper.mab004Id}`}>
												<SortableItem 
													id={grouper.mab004Id.toString()} 
													key={grouper.mab004Id} 
													disabled={waitingForResponse}
												>
													<Groupers 
														grouperData={grouper}
														selectedMarketShare={data}
														refModalGrouper={refModalGrouper}
														refModalGraphic={refModalGraphicGrouper}
													/>
												</SortableItem>
											</div>
										))}
									</SortableContainer>
								</>
							)
						}
					</RCCGrid>
				</>
			)}

			<ModalGrouper 
				onCallbackAddOrChange={onCallbackAddOrChange}
				onCallbackDelete={onCallbackDelete}
				ref={refModalGrouper} 
			/>

			<ModalGraphic 
				ref={refModalGraphic} 
			/>

			<ModalGrouperGraphic 
				ref={refModalGraphicGrouper} 
			/>

		</>
	);
};


export default React.forwardRef(ContentMarkShare);
